import { ReactNode, useEffect, useState } from 'react';
import { Sidebar } from './layout/Sidebar';
import { Topbar } from './layout/Topbar';
import { CommandPalette } from './ui/command-palette';
import { useNavigate } from 'react-router-dom';
import { getOverview } from '../services/api';

export type ApiStatus = 'checking' | 'online' | 'offline';

export const AppLayout = ({ title, children, fullBleed = false }: { title: string; children: ReactNode; fullBleed?: boolean }) => {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [apiStatus, setApiStatus] = useState<ApiStatus>('checking');
  const navigate = useNavigate();

  useEffect(() => { document.title = `${title} | Telemed Control Center`; }, [title]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    let active = true;
    const checkApi = () => {
      getOverview()
        .then(() => active && setApiStatus('online'))
        .catch(() => active && setApiStatus('offline'));
    };
    checkApi();
    const timer = window.setInterval(checkApi, 30_000);
    return () => { active = false; window.clearInterval(timer); };
  }, []);

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Topbar apiStatus={apiStatus} onOpenMenu={() => setSidebarOpen(true)} onOpenPalette={() => setPaletteOpen(true)} />
        <main className={fullBleed ? 'min-h-0 flex-1 overflow-hidden' : 'grid-noise flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8'}>
          <div className={fullBleed ? 'h-full min-h-0' : 'mx-auto max-w-[1540px]'}>
            {children}
          </div>
        </main>
      </div>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} onNavigate={(path) => navigate(path)} />
    </div>
  );
};
